import { useEffect } from "react";

type SeoHeadOptions = {
  title: string;
  description?: string;
  canonical?: string;
  jsonLd?: Array<Record<string, unknown>>;
};

// Keeps the per-route <title>, meta description, and canonical URL in sync
// during client-side navigation so crawlers and users see the right tab
// title even when the server HTML (from the SPA fallback) was written for
// a different route. JSON-LD blocks are injected with stable ids so the
// next mount can remove them, avoiding duplicate structured data.
export function useSeoHead({ title, description, canonical, jsonLd }: SeoHeadOptions) {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = title;

    const setMeta = (name: string, content: string, attr: "name" | "property" = "name") => {
      let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      const prev = el.content;
      el.content = content;
      return () => {
        el!.content = prev;
      };
    };

    const restorers: Array<() => void> = [];
    if (description) {
      restorers.push(setMeta("description", description));
      restorers.push(setMeta("og:description", description, "property"));
      restorers.push(setMeta("twitter:description", description));
    }
    restorers.push(setMeta("og:title", title, "property"));
    restorers.push(setMeta("twitter:title", title));

    if (canonical) {
      let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
      const prevHref = link?.href ?? null;
      if (!link) {
        link = document.createElement("link");
        link.rel = "canonical";
        document.head.appendChild(link);
      }
      link.href = canonical;
      restorers.push(() => {
        if (prevHref) link!.href = prevHref;
      });

      const ogUrl = setMeta("og:url", canonical, "property");
      restorers.push(ogUrl);
    }

    const injectedIds: string[] = [];
    (jsonLd ?? []).forEach((obj, i) => {
      const id = `ld-dynamic-${i}`;
      const existing = document.getElementById(id);
      if (existing) existing.remove();
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.id = id;
      s.text = JSON.stringify(obj);
      document.head.appendChild(s);
      injectedIds.push(id);
    });

    return () => {
      document.title = prevTitle;
      restorers.forEach((r) => r());
      injectedIds.forEach((id) => document.getElementById(id)?.remove());
    };
    // jsonLd is JSON-stringified to avoid reference-equality churn when
    // callers build the array inline on each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, canonical, JSON.stringify(jsonLd ?? null)]);
}
